import { useTheme } from '@mui/material/styles'
import type { ComponentProps, ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Box } from './Box.tsx'
import { Collapse, Typography } from './generic.tsx'
import { Icon, type IconName } from './Icon.tsx'
import { Stack } from './Stack.tsx'

const MIN_SIZE = 24

export const IconStack = <T extends { id: string; expandedContent?: ReactNode; expand?: boolean } & IconStackItemProps>({
	items,
	maxItems = 5,
	sx,
	...props
}: { items: T[]; maxItems?: number } & ComponentProps<typeof Box>) => {
	const [expandedItemId, setExpandedItemId] = useState<string | null>(null)

	useEffect(() => {
		if (items.length > 0 && items[0]) setExpandedItemId(items[0].id)
	}, [items])

	const extraItemsCount = Math.max(0, items.length - maxItems)

	return (
		<Box flex center sx={{ '& > *:not(:first-of-type)': { ml: -1 }, ...sx }} {...props}>
			{items.slice(0, maxItems).map((item, index) => {
				const { id, expandedContent, expand = false, ...iconStackItemProps } = item
				const shouldExpand = (expand || expandedItemId === id) && Boolean(expandedContent)
				return (
					<IconStackItem
						key={id}
						expanded={shouldExpand}
						index={index}
						onMouseEnter={() => {
							if (!expandedContent) return
							setExpandedItemId(id)
						}}
						{...iconStackItemProps}
					>
						{expandedContent}
					</IconStackItem>
				)
			})}
			{extraItemsCount > 0 && (
				<Typography variant='body2' color='text.secondary' alignContent={'center'} ml={0.25}>{`+${extraItemsCount}`}</Typography>
			)}
		</Box>
	)
}

interface IconStackItemProps {
	icon: IconName
	iconColor?: string
	fontColor?: string
	bgColor?: string
	variant?: 'filled' | 'outlined'
}

const IconStackItem = ({
	expanded,
	index,
	onMouseEnter,
	icon,
	iconColor,
	fontColor,
	bgColor,
	variant = 'filled',
	children,
}: {
	expanded: boolean
	index: number
	onMouseEnter?: () => void
	children?: ReactNode
} & IconStackItemProps) => {
	const theme = useTheme()
	const [contentWidth, setContentWidth] = useState(MIN_SIZE)
	const contentRef = useRef<HTMLDivElement>(null)

	const variantStyles = theme.palette.iconStack[variant]
	const finalBgColor = bgColor ?? variantStyles.bgColor
	const finalIconColor = iconColor ?? variantStyles.iconColor
	const finalFontColor = fontColor ?? variantStyles.fontColor

	// Dynamic width calculation
	const updateContentWidth = useCallback(() => {
		if (contentRef.current) {
			const newWidth = contentRef.current.scrollWidth + MIN_SIZE * 1.5
			setContentWidth((prev) => Math.max(prev, newWidth))
		}
	}, [])

	useEffect(() => {
		if (expanded) {
			updateContentWidth()
		}
		if (contentRef.current) {
			const observer = new ResizeObserver(updateContentWidth)
			observer.observe(contentRef.current)
			return () => observer.disconnect()
		}
	}, [expanded, updateContentWidth])

	return (
		<Stack
			center
			borderRadius='2rem'
			bgcolor={finalBgColor}
			px={expanded ? 1 : 0}
			gap={expanded ? 1 : 0}
			sx={{
				height: MIN_SIZE,
				minWidth: MIN_SIZE,
				width: expanded ? contentWidth : MIN_SIZE,
				overflow: 'hidden',
				border: variant === 'outlined' ? `1px solid ${finalIconColor}` : 'none',
				outline: (theme) => (variant === 'filled' ? `1px solid ${theme.palette.background.paper}` : 'none'),
				transition: (theme) =>
					theme.transitions.create(['width', 'background-color', 'border-radius', 'gap'], {
						duration: theme.transitions.duration.standard,
					}),
			}}
			onMouseEnter={onMouseEnter}
			zIndex={expanded ? 101 : 100 - index}
		>
			<Icon name={icon} sx={{ fontSize: 12, color: `${finalIconColor}` }} />
			{expanded && children && (
				<Collapse in={expanded} orientation='horizontal'>
					<Typography component='span' variant='body2' color={finalFontColor} noWrap>
						<Box ref={contentRef} flex gap={0.5} center>
							{children}
						</Box>
					</Typography>
				</Collapse>
			)}
		</Stack>
	)
}
